"use client";

import { MAX_ADDRESS_LENGTH } from "@/app/common/constants";
import {
  type IntakeData,
  useIntakeForm,
} from "@/app/directories/companion/components_v2/AppointmentForm/hooks/useIntakeForm";
import { IntakeModalPdf } from "@/app/directories/companion/components_v2/IntakeModalPdf";
import { triggerUserMessageErrorToast } from "@/app/directories/companion/components_v2/ToastHelpers";
import { convertToMMddYYYY } from "@/app/directories/shop/lib/MarketCore";
import { datadogLogs } from "@datadog/browser-logs";
import type {
  IntakeFormData,
  IntakeFormQuestionTypes,
  PolymorphicSavedResponse,
} from "@frontend/components/intake/Intake";
import {
  getOrgById,
  getRetrievableIntakeResponsesByIds,
  saveIntakeFormResponse,
  writeIntakeFormToEhr,
} from "@frontend/lib/actions/actions";
import {
  EVENT_CATEGORIES,
  EVENT_CONTEXTS,
  trackEvent,
} from "@frontend/lib/analytics";
import { SERVICE_NAMES } from "@frontend/lib/apollo-client";
import type {
  GetOrgAndPracticesByOrgIdQuery,
  LanguageCode,
} from "@frontend/lib/graphql/generated-gql";
import type {
  GetAppointmentsForOrgIdQueryConsumer as Consumer,
  GetIntakeFormsAndQuestionsByFormIdsQueryFormToQuestion as FormToQuestion,
  GetIntakeFormsAndQuestionsByFormIdsQueryQuestion as IntakeQuestion,
} from "@frontend/lib/graphql/gql-types";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { genericQuery } from "@frontend/lib/queries/generic";
import { getUserById } from "@frontend/lib/queries/user/getUserById";
import type { ConsumerData } from "@frontend/lib/schemas/clientSchema";
import { Button } from "@frontend/shared/components/ui/button";
import { Separator } from "@frontend/shared/components/ui/separator";
import { useToast } from "@frontend/shared/components/ui/use-toast";
import { chunkArray, cn, throwIfNull } from "@frontend/shared/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePDF } from "@react-pdf/renderer";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { differenceInYears, format, parseISO } from "date-fns";
import { DownloadIcon, Loader2, Save, X } from "lucide-react";
import React, {
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { z } from "zod";
import { IntakeFormQuestion } from "./IntakeFormQuestion";
import type { fieldSchema } from "./IntakeFormTypes";
import { generateMultiPagePDF } from "./generateOptimizedPdf";

export const createDynamicFormSchema = (
  fields: z.infer<typeof fieldSchema>[] | undefined,
) => {
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  if (fields) {
    for (const field of fields) {
      let fieldSchema: z.ZodTypeAny;

      const choiceEnum =
        field.type === "SINGLE_SELECT" || field.type === "MULTI_SELECT"
          ? field.questionAttributes.choices?.map((c) => c.id)
          : undefined;

      const drawnSignatureSchema = z.object({
        dataUrl: z.string(), // check for b64 schema elem
      });

      const typedSignatureSchema = z.object({
        text: z.string(),
        style: z.string().optional(),
      });

      const legacySignatureSchema = z.string();

      switch (field.type) {
        case "SIGNATURE":
          fieldSchema = z.object({
            signature: z.union([
              drawnSignatureSchema,
              typedSignatureSchema,
              legacySignatureSchema,
            ]),
            isSignatureComplete: z.boolean().refine((val) => val === true, {
              message: "PleaseSignYourName",
            }),
            date: z.string(),
          });
          break;
        case "TEXT":
          if (field.isRequired) {
            fieldSchema = z
              .string()
              .trim()
              .min(1, { message: "MustContainAtLeastOne" });
          } else {
            fieldSchema = z.string().trim().optional();
          }
          break;
        case "DATE":
          if (field.isRequired) {
            fieldSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
          } else {
            fieldSchema = z
              .string()
              .regex(/^\d{4}-\d{2}-\d{2}$/)
              .or(z.literal(""))
              .nullish();
          }
          break;
        /**
         * TODO: Change this based on a real spec https://shipsuperscript.atlassian.net/browse/SUP-1584
         */
        case "SINGLE_SELECT":
          if (field.isRequired) {
            fieldSchema = z.enum(choiceEnum as [string, ...string[]], {
              errorMap: (issue, ctx) => ({
                message: "YouMustSelectAnOption",
              }),
            });
          } else {
            fieldSchema = z.union([
              z.enum(choiceEnum as [string, ...string[]]),
              z.literal(""),
              z.null(),
            ]);
          }
          break;
        case "MULTI_SELECT":
          if (field.isRequired) {
            fieldSchema = z
              .array(z.enum(choiceEnum as [string, ...string[]]))
              .nonempty({ message: "YouMustSelectAtLeastOne" });
          } else {
            fieldSchema = z.union([
              z.array(z.enum(choiceEnum as [string, ...string[]]).optional()),
              z.literal(""),
              z.null(),
            ]);
          }
          break;
        case "CHECKBOX":
          if (field.isRequired) {
            fieldSchema = z.union([
              z.literal("true", {
                errorMap: (issue, ctx) => ({
                  message: "You must check in order to continue",
                }),
              }),
              z.literal(true, {
                errorMap: (issue, ctx) => ({
                  message: "You must check in order to continue",
                }),
              }),
            ]);
          } else {
            fieldSchema = z.union([
              z.literal("false"),
              z.literal(false),
              z.literal("true"),
              z.literal(true),
            ]);
          }
          break;
        case "LONG_TEXT":
          if (field.isRequired) {
            fieldSchema = z
              .string()
              .trim()
              .min(1, { message: "MustContainAtLeastOne" });
          } else {
            fieldSchema = z.any();
          }
          break;
        case "ADDRESS":
          if (field.isRequired) {
            fieldSchema = z
              .string({ message: "You must enter an address" })
              .trim()
              .min(1, { message: "You must enter an address" })
              .max(MAX_ADDRESS_LENGTH, {
                message: `Address must be ${MAX_ADDRESS_LENGTH} characters or less`,
              });
          } else {
            fieldSchema = z
              .string()
              .trim()
              .max(MAX_ADDRESS_LENGTH, {
                message: `Address must be ${MAX_ADDRESS_LENGTH} characters or less`,
              })
              .optional();
          }
          break;
        case "IMAGE":
          fieldSchema = z
            .string({ message: "YouMustUploadAnImage" })
            .regex(/^data:image\/[a-zA-Z]+;base64,([^\s]+)$/, {
              message: "YouMustUploadAnImage",
            });
          break;
        case "LIST":
          fieldSchema = z.array(
            z.object({
              ehrId: z.number().optional(),
              value: z.string(),
            }),
          );
          break;
        case "PHONE_NUMBER":
          if (field.isRequired) {
            fieldSchema = z.string();
          } else {
            fieldSchema = z.string().or(z.literal(""));
          }
          break;
        case "CLINICAL_PROVIDER":
          if (field.isRequired) {
            fieldSchema = z.object({
              name: z.string(),
              firstName: z.string().optional(),
              lastName: z.string().optional(),
              address: z
                .object({
                  streetAddress: z.string().optional(),
                  city: z.string().optional(),
                  state: z.string().optional(),
                  zip: z.string().optional(),
                })
                .nullish(),
              phone: z.string().optional(),
              ehrId: z.number().or(z.string()).optional(),
              ncpdpId: z.number().or(z.string()).optional(),
            });
          } else {
            fieldSchema = z
              .object({
                name: z.string(),
                firstName: z.string().optional(),
                lastName: z.string().optional(),
                address: z
                  .object({
                    streetAddress: z.string().optional(),
                    city: z.string().optional(),
                    state: z.string().optional(),
                    zip: z.string().optional(),
                  })
                  .nullish(),
                phone: z.string().optional(),
                ehrId: z.number().or(z.string()).optional(),
                ncpdpId: z.number().or(z.string()).optional(),
              })
              .nullish();
          }
          break;
        default:
          fieldSchema = z.any();
      }

      if (!field.isRequired) {
        schemaFields[field.id] = fieldSchema.optional();
      } else {
        schemaFields[field.id] = fieldSchema;
      }
    }
  }

  return z.object(schemaFields);
};

export interface visitData {
  providerName?: string;
  location: {
    address: string;
    phoneNumber?: string | null;
    locationName: string;
  };
  startTime?: Date;
}

export interface RefMap {
  [key: string]: RefObject<HTMLDivElement | null>;
}

export type IntakeQuestionChoice = {
  id: string;
  value: string;
  conditionalQuestions: number[];
  critical: boolean;
};

export type IntakeQuestionAttributes = {
  blurb?: string;
  min?: string;
  max?: string;
  choices?: IntakeQuestionChoice[];
  hiddenByDefault: boolean;
};

export const responseIsPrefilled = (
  q: IntakeQuestion,
  choice: IntakeQuestionChoice,
  previousResponses: Record<number, PolymorphicSavedResponse>,
) => {
  return (
    q.id in previousResponses &&
    ((q.type === "SINGLE_SELECT" &&
      (previousResponses[q.id].response as string) === choice.id) ||
      (q.type === "MULTI_SELECT" &&
        (previousResponses[q.id].response as string[]).includes(choice.id)))
  );
};

export const formatPreviousResponse = (
  response: PolymorphicSavedResponse,
  questionType: IntakeFormQuestionTypes,
  questionId?: number,
) => {
  switch (questionType) {
    case "SIGNATURE":
      return response.richResponse;
    case "MULTI_SELECT": {
      try {
        if (Array.isArray(response.response)) {
          return response.response;
        }

        const responseArray = JSON.parse(response.response);
        if (Array.isArray(responseArray)) {
          const responseNumberArray = responseArray.filter(
            (res) => typeof Number.parseInt(res) === "number",
          );

          return responseNumberArray;
        }
        datadogLogs.logger.info(
          "Invalid response object for intake multi-select question",
          {
            questionType: questionType,
            response: response.response,
          },
        );
      } catch (error) {
        datadogLogs.logger.info(
          "Invalid parsing of response object for intake multi-select question",
          {
            questionType: questionType,
            response: response.response,
            error: error,
          },
        );
        return [];
      }
      return [];
    }
    default:
      return response.response;
  }
};

export const formatIntakeForm = (
  intakeData: IntakeData[],
  retrievableIntakeResponses: Record<number, PolymorphicSavedResponse>,
  index?: number,
) => {
  const {
    form: { intakeFormToQuestions },
    response,
  } = intakeData.at(index ?? 0) ?? {
    form: {
      intakeFormToQuestions: [] as FormToQuestion[],
    },
    response: undefined,
  };

  const prefillInfo = (response?.responses ?? {}) as Record<
    number,
    PolymorphicSavedResponse
  >;

  const intakeFormQuestions = intakeFormToQuestions.map((x) => ({
    ...x,
    ...x.intakeQuestion,
  }));

  const previousResponses = {
    ...retrievableIntakeResponses,
    ...prefillInfo,
  };

  const unlockedQuestions = new Set(
    intakeFormQuestions
      .filter((q) => q.type === "SINGLE_SELECT" || q.type === "MULTI_SELECT")
      .reduce(
        (accum, q) =>
          accum.concat(
            // biome-ignore lint/style/noNonNullAssertion: <explanation>
            q.questionAttributes
              .choices!.filter((c) =>
                responseIsPrefilled(q, c, previousResponses),
              )
              .flatMap((c) => c.conditionalQuestions),
          ),
        [] as number[],
      ),
  );

  const tempHiddenQuestions = new Set(
    intakeFormQuestions
      .filter(
        (q) =>
          q.questionAttributes.hiddenByDefault && !unlockedQuestions.has(q.id),
      )
      .map((q) => q.id),
  );

  const defaultValues = intakeFormQuestions.reduce((accum, q) => {
    if (q.id in previousResponses && !tempHiddenQuestions.has(q.id)) {
      accum[q.id] = formatPreviousResponse(previousResponses[q.id], q.type);
    }
    return accum;
  }, {});

  const questionsWithUpdatedAt = intakeFormQuestions.map((q) => ({
    ...q,
    updatedAt:
      q.id in defaultValues && defaultValues[q.id]
        ? defaultValues[q.id].updatedAt && !(q.id in prefillInfo)
          ? parseISO(defaultValues[q.id].updatedAt)
          : undefined
        : undefined,
  }));

  const groupedQuestions = chunkArray(
    questionsWithUpdatedAt,
    (lastItem, newItem) => lastItem.section === newItem.section,
  );

  const sectionedQuestions =
    intakeFormQuestions.length === 0
      ? []
      : groupedQuestions.map((group) => ({
          section: group[0].section,
          questions: group,
        }));

  return {
    intakeFormQuestions,
    tempHiddenQuestions,
    defaultValues,
    sectionedQuestions,
  };
};

export const IntakeForm = ({
  consumer,
  userLocale,
  appointmentCreatedAt,
  appointmentId,
  consumerId,
  userId,
  intakeForm,
  nextForm,
  setSelectedForm,
  setAnsweredQuestions,
  orgId,
  isAdminView,
  isFormComplete,
  isCompleted,
}: {
  consumer: ConsumerData;
  userLocale: LanguageCode;
  appointmentCreatedAt: Date;
  appointmentId: string;
  consumerId: string;
  userId: string;
  intakeForm: { name: string; id: number };
  nextForm: IntakeFormData | null;
  setSelectedForm: (newValue: IntakeFormData | null) => void;
  setAnsweredQuestions: (newValue: number) => void;
  orgId: number;
  isAdminView: boolean;
  isFormComplete?: boolean;
  isCompleted?: boolean;
}) => {
  const { toast, dismiss } = useToast();
  const displayedToastId = useRef<string | null>(null);
  const displayingErrorForQuestionId = useRef<string | null>(null);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [hiddenQuestions, setHiddenQuestions] = useState<Set<number>>(
    new Set(),
  );
  const [isIntakeBeingSaved, setIsIntakeBeingSaved] = useState(false);
  const [isSavingIncompleteForm, setIsSavingIncompleteForm] = useState(false);

  const englishFormSectionsRef = useRef<RefMap>({});

  const t = useTranslations("IntakeFormStates");

  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [shouldOpenPDF, setShouldOpenPDF] = useState(false);

  const [instance, update] = usePDF({
    //@ts-ignore
    document: null,
  });

  // Watch for changes in instance.url and shouldOpenPDF
  // This is based on the fact that the update function on the usePDF is a setState operation that
  // has to rerender for the changes to the instance to impart
  useEffect(() => {
    if (instance.url && shouldOpenPDF && !instance.loading) {
      window.open(instance.url, "_blank");
      setShouldOpenPDF(false); // Reset the flag
      setIsGeneratingPDF(false);
    }
  }, [instance, instance.url, instance.loading, shouldOpenPDF]);

  const { data: prefetchedUserData } = useQuery({
    queryKey: ["getUserById", userId],
    queryFn: () => getUserById(userId),
    enabled: !isAdminView,
  });

  const {
    data: { findFirstOrganization: prefetchedOrgData },
  } = useQuery({
    queryKey: ["GetOrgAndPracticesByOrgId", orgId],
    queryFn: () =>
      genericQuery("GetOrgAndPracticesByOrgIdDocument", SERVICE_NAMES.pricing, {
        orgId,
      }) as Promise<GetOrgAndPracticesByOrgIdQuery>,
    enabled: !isAdminView,
    initialData: { findFirstOrganization: undefined },
  });

  const { intakeData } = useIntakeForm(
    [intakeForm.id],
    appointmentId,
    "en" as LanguageCode,
  );

  const questionIds =
    intakeData
      .at(0)
      ?.form.intakeFormToQuestions.map((q) => q.intakeQuestion.id) ?? [];

  const { data: retrievableIntakeResponses } = useQuery({
    queryKey: ["getRetrievableIntakeResponsesByIds", questionIds, consumerId],
    queryFn: () => getRetrievableIntakeResponsesByIds(questionIds, consumerId),
    enabled: questionIds.length > 0 && !isAdminView,
    initialData: [],
    select: (data) =>
      data.reduce(
        (accum, x) => {
          accum[x.intakeQuestionId] = {
            response: x.response,
          };
          return accum;
        },
        {} as Record<number, PolymorphicSavedResponse>,
      ),
  });

  const {
    intakeFormQuestions,
    tempHiddenQuestions,
    defaultValues,
    sectionedQuestions,
  } = useMemo(
    () => formatIntakeForm(intakeData, retrievableIntakeResponses),
    [intakeData, retrievableIntakeResponses],
  );

  useEffect(() => {
    setHiddenQuestions(tempHiddenQuestions);
  }, [tempHiddenQuestions]);

  sectionedQuestions.forEach((section, sectionIndex) => {
    section.questions.forEach((question, questionIndex) => {
      const physicalFormIndex = Number.parseInt(
        `${sectionIndex}00${questionIndex}`,
      );
      englishFormSectionsRef.current[physicalFormIndex] =
        React.createRef<HTMLDivElement>();
    });
  });

  const handlePrintReceipt = async () => {
    try {
      update(
        <IntakeModalPdf
          consumer={consumer}
          formName={intakeForm.name}
          sectionedQuestions={sectionedQuestions}
          defaultValues={defaultValues}
          userLocale={userLocale}
          timeCreated={intakeData.at(0)?.response?.createdAt}
        />,
      );
      setShouldOpenPDF(true); // Set flag to open PDF once instance updates
    } catch (error) {
      triggerUserMessageErrorToast(
        consumer.firstname,
        consumer.lastname,
        `Error generating receipt:"${error}`,
        "Failed to generate receipt",
      );
      setIsGeneratingPDF(false);
    }
  };

  const dynamicFormSchema = useMemo(() => {
    const shownQuestions = intakeFormQuestions.filter(
      (question) => !hiddenQuestions.has(question.id),
    );

    return createDynamicFormSchema(shownQuestions);
  }, [intakeFormQuestions, hiddenQuestions]);

  type DynamicFormType = z.infer<typeof dynamicFormSchema>;

  const form = useForm<Partial<DynamicFormType>>({
    shouldUseNativeValidation: false,
    resolver: zodResolver(dynamicFormSchema),
    defaultValues,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    trigger,
    setFocus,
    clearErrors,
    formState: { errors },
  } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Needed to handle re-creating the form validation when hiding / unhiding questions
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    reset(defaultValues, {
      keepErrors: true,
      keepDirtyValues: true,
    });
  }, [defaultValues]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      errors &&
      displayedToastId.current &&
      displayingErrorForQuestionId.current
    ) {
      if (!Object.keys(errors).includes(displayingErrorForQuestionId.current)) {
        dismiss(displayedToastId.current);
        displayedToastId.current = null;
        displayingErrorForQuestionId.current = null;
      }
    }
  }, [Object.keys(errors).length, errors]);

  const queryClient = useQueryClient();

  const formatAnswers = (data: Partial<DynamicFormType>) => {
    return Object.entries(data).reduce((accum, [key, value]) => {
      const originalQuestion = intakeFormQuestions.find(
        (questionObject) => questionObject.id.toString() === key,
      );

      if (!originalQuestion || (!value && !defaultValues[key])) {
        return accum;
      }

      const richResponse =
        originalQuestion.type === "SIGNATURE"
          ? { signature: value.signature, ...value }
          : null;

      const formattedResponse = (() => {
        switch (originalQuestion.type) {
          case "SIGNATURE":
            return `signed on ${value.date}`;
          default:
            return typeof value === "string" ? value : JSON.stringify(value);
        }
      })();

      // const choiceIndex = originalQuestion.type === "SINGLE_SELECT" ?  : null
      accum[key] = {
        questionText: originalQuestion.questionText,
        questionNumber: originalQuestion.questionNumber,
        response: formattedResponse,
        richResponse: richResponse,
      };

      return accum;
    }, {} as DynamicFormType);
  };

  /**
   * Trigger is manual because we are overriding the form behavior
   * @param data
   * @returns
   */
  const onSubmit = async (data: DynamicFormType) => {
    cancelAutosave();
    if (isSavingIncompleteForm) {
      setIsSavingIncompleteForm(false);
    }

    setIsIntakeBeingSaved(true);
    const isValid = await trigger();
    if (!isValid) {
      setIsIntakeBeingSaved(false);
      return;
    }

    const formattedAnswer = formatAnswers(data);

    /**
     * Turn signature sections into pdfs and upload it to AWS
     */
    const formData = new FormData();

    const questionDoms = Object.values(englishFormSectionsRef.current)
      .map((ref) => ref.current)
      .filter((dom) => !!dom);

    if (questionDoms.length) {
      const intakeQuestionMap = new Map(
        intakeFormQuestions.map((question) => [question.id, question]),
      );

      const orgData = throwIfNull(
        prefetchedOrgData ?? (await getOrgById(orgId)),
      );

      const FormArrayBuffer = await generateMultiPagePDF(
        questionDoms,
        intakeQuestionMap,
      );
      const blob = new Blob([FormArrayBuffer], { type: "application/pdf" });
      const eSignatureFileName = `${orgData.orgName}/${format(
        appointmentCreatedAt,
        "yyyy-MM-dd",
      )}-[${appointmentId}]-${intakeForm.id}-${Date.now()}`;
      formData.append("file", blob);
      formData.append("name", eSignatureFileName);
      formData.append("fileName", intakeForm.name);
      formData.append("appointmentId", appointmentId);
      formData.append("orgId", orgId.toString());
      formData.append("formId", intakeForm.id.toString());
    }

    const [, res] = await Promise.all([
      writeIntakeFormToEhr(formData, userId),
      saveIntakeFormResponse(
        intakeForm.name,
        intakeForm.id,
        appointmentId,
        consumerId,
        userId,
        formattedAnswer,
        true, // isCompleted
        orgId,
        undefined,
      ),
    ]);

    if (res) {
      toast({
        title: t("FormSubmissionSuccess"),
        duration: 3000,
        description: t("FormSubmittedCorrectly", { name: intakeForm.name }),
        style: {
          border: "1px solid green",
          backgroundColor: "#DBF8EF",
        },
      });
      setSelectedForm(nextForm);
      if (nextForm === null) {
        const userData = throwIfNull(
          prefetchedUserData ?? (await getUserById(userId)),
        );
        // removed sendAndLogMessage for INTAKE_COMPLETE
      }
      await queryClient.refetchQueries({
        queryKey: [
          "getIntakeFormsAndQuestionsByFormIds",
          appointmentId,
          [intakeForm.id],
          userLocale,
        ],
        type: "all",
      });

      await queryClient.refetchQueries({
        queryKey: ["getIntakeFormResponse", appointmentId, [intakeForm.id]],
        type: "all",
      });
    }

    setIsIntakeBeingSaved(false);
  };

  // autosave debounce 10 seconds
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const initiateAutosave = useCallback(
    (data: Partial<DynamicFormType>, hiddenQuestions?: Set<number>) => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }

      autosaveTimeoutRef.current = setTimeout(async () => {
        await saveIncompleteForm(data, false, hiddenQuestions);
      }, 10000);
    },
    [intakeFormQuestions],
  );

  const cancelAutosave = () => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);

  const saveIncompleteForm = async (
    data: Partial<DynamicFormType>,
    manualSave = false,
    localHiddenQuestions?: Set<number>,
  ) => {
    if (isIntakeBeingSaved) {
      return;
    }

    setIsSavingIncompleteForm(true);
    const formattedAnswer = formatAnswers(data);

    const currentHiddenQuestions = localHiddenQuestions ?? hiddenQuestions;

    if (!manualSave) {
      // Check if there are any actual changes compared to defaultValues
      const changedFields = Object.entries(data).reduce(
        (acc, [key, value]) => {
          const defaultValue = defaultValues[key];
          let hasChanged = false;

          // Handle different types of values
          if (value === undefined || value === null) {
            hasChanged = defaultValue !== undefined && defaultValue !== null;
          }
          // Handle signature objects
          else if (typeof value === "object") {
            hasChanged = JSON.stringify(value) !== JSON.stringify(defaultValue);
          }
          // Handle arrays (e.g., multi-select)
          else if (Array.isArray(value)) {
            hasChanged = JSON.stringify(value) !== JSON.stringify(defaultValue);
          }
          // Handle primitive values
          else {
            hasChanged = value !== defaultValue;
          }

          if (hasChanged) {
            acc[key] = {
              old: defaultValue,
              new: value,
            };
          }
          return acc;
        },
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        {} as Record<string, { old: any; new: any }>,
      );

      const hasChanges = Object.keys(changedFields).length > 0;
      if (!hasChanges) {
        setIsSavingIncompleteForm(false);
        return;
      }

      // Get all answered questions regardless of section
      const answeredFields = Object.entries(data).reduce((acc, [id, value]) => {
        // Skip hidden questions
        if (currentHiddenQuestions.has(Number(id))) {
          return acc;
        }

        const isAnswered = (() => {
          // Handle signature fields
          if (value && typeof value === "object" && "date" in value) {
            return value.signature && value.date;
          }
          // Handle arrays (e.g., for multi-select)
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          // Handle all other fields
          return value !== undefined && value !== null && value !== "";
        })();

        if (isAnswered) {
          acc.push(id);
        }

        return acc;
      }, [] as string[]);

      if (answeredFields.length > 0) {
        // Check if any of the answered fields have changes
        const hasChanges = answeredFields.some(
          (fieldId) => fieldId in changedFields,
        );

        if (!hasChanges) {
          if (intakeFormQuestions.length === 0) {
            datadogLogs.logger.info(
              "IntakeForm: attempt autosave but intakeFormQuestions is empty",
              { userId, appointmentId, intakeFormId: intakeForm.id },
            );
          }
          setIsSavingIncompleteForm(false);
          return;
        }

        const isValid = await trigger(answeredFields);
        if (!isValid) {
          return;
        }
      } else {
        // No answered fields to validate
        setIsSavingIncompleteForm(false);
        return;
      }
    }

    const res = await saveIntakeFormResponse(
      intakeForm.name,
      intakeForm.id,
      appointmentId,
      consumerId,
      userId,
      formattedAnswer,
      false, // isCompleted
      orgId,
      undefined,
    );

    if (res) {
      if (manualSave) {
        toast({
          title: "Saved!",
          description: `${intakeForm.name} progress saved successfully.`,
          style: {
            border: "1px solid green",
            backgroundColor: "#DBF8EF",
          },
        });

        await queryClient.refetchQueries({
          queryKey: ["getIntakeFormsByFormIds"],
          type: "all",
        });

        await queryClient.refetchQueries({
          queryKey: ["getIntakeFormResponse", appointmentId, [intakeForm.id]],
          type: "all",
        });
      }
    } else {
      if (manualSave) {
        toast({
          title: "Error",
          description: `${intakeForm.name} save failed. Please contact support@superscript.nyc if this issue persists.`,
          style: {
            border: "1px solid red",
            backgroundColor: "#fff4f6",
          },
        });
      }
    }
    setIsSavingIncompleteForm(false);
  };

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const t_intakeForm = useTranslations("IntakeForm");

  const handleErrors = (errors: FieldErrors<DynamicFormType>) => {
    const errorKeys = Object.keys(errors);

    if (errorKeys.length > 0) {
      const firstError = errorKeys[0];
      const firstErrorQuestion = intakeFormQuestions.find(
        (question) => question.id.toString() === firstError,
      );

      if (firstErrorQuestion) {
        // Find the section that contains this question
        const questionSection = sectionedQuestions.find((section) =>
          section.questions.some((q) => q.id.toString() === firstError),
        );

        displayingErrorForQuestionId.current = firstError;
        const { id: toastId } = toast({
          title: t_intakeForm("ErrorOnQuestion"),
          description: questionSection
            ? `${questionSection.section} - ${firstErrorQuestion.questionText}`
            : firstErrorQuestion.questionText,
          duration: Number.POSITIVE_INFINITY,
          style: {
            border: "1px solid red",
            backgroundColor: "#fff4f6",
          },
        });
        displayedToastId.current = toastId;

        if (firstError) {
          setFocus(firstError);
          document.getElementsByName(firstError)[0]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
        setIsIntakeBeingSaved(false);
      }
    }
  };

  return (
    <>
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();

          for (const [key, value] of Object.entries(getValues())) {
            if (Array.isArray(value) && value?.length === 0) {
              setValue(key, []);
            } else if (value === "") {
              setValue(key, undefined);
            }
          }

          handleSubmit(onSubmit, handleErrors)(e);
        }}
        onKeyDown={(e) => checkKeyDown(e)}
        className="overflow-y-scroll no-scrollbar h-fit"
      >
        <button
          type="button"
          className="absolute top-6 right-6  rounded-full sm:px-2 sm:py-2 text-black hover:text-gray-500 hover:cursor-pointer"
          onClick={() => {
            setSelectedForm(null);
          }}
        >
          <X className="cursor-pointer" strokeWidth={2} size={20} />
        </button>

        <div className="flex flex-col">
          <div className="flex gap-2 items-center mb-2 mt-6">
            <p className="text-2xl font-medium">{intakeForm.name}</p>
            {isCompleted && (
              <div className="rounded-full border border-black text-xs font-semibold px-4 py-1.5">
                Completed
              </div>
            )}
          </div>

          <p className="text-sm mb-8">{t_intakeForm("Required")}</p>

          {sectionedQuestions.map((section, i) => (
            <IntakeFormQuestion
              key={section.section}
              index={i}
              sectionName={section.section}
              sectionedFormQuestion={section.questions}
              questionRef={englishFormSectionsRef}
              control={control}
              register={register}
              errors={errors}
              clearErrors={clearErrors}
              getValues={getValues}
              setValue={setValue}
              trigger={trigger}
              hiddenQuestions={hiddenQuestions}
              setHiddenQuestions={setHiddenQuestions}
              setAnsweredQuestions={setAnsweredQuestions}
              isAdmin={isAdminView}
              isPrint={false}
              initiateAutosave={initiateAutosave}
              isFormComplete={isFormComplete}
            />
          ))}
        </div>

        {!isAdminView && (
          <div className="fixed bottom-0 lg:w-[78vw] min-h-[40px] sm:w-full right-0 py-2 px-6 bg-white border-t border-gray-200 mt-8">
            <div className="w-full flex justify-end items-center gap-4">
              {!isFormComplete ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-md border-black px-4 py-2"
                    onClick={() => saveIncompleteForm(getValues(), true)}
                  >
                    {isSavingIncompleteForm ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <Save size={20} />
                    )}
                  </Button>
                  <Button
                    type="submit"
                    className={cn(
                      "relative overflow-hidden rounded-md px-12 py-3 min-w-[120px]",
                      isIntakeBeingSaved
                        ? "bg-black"
                        : "bg-black hover:bg-black/90",
                    )}
                  >
                    <div
                      className={cn(
                        "absolute inset-0 bg-superscriptBlue transition-transform ease-in-out",
                        isIntakeBeingSaved
                          ? "translate-x-0"
                          : "-translate-x-full",
                      )}
                      style={{ transitionDuration: "5s" }}
                    />

                    <p className="text-white text-sm relative z-10">
                      {isIntakeBeingSaved
                        ? t_intakeForm("Saving")
                        : nextForm
                          ? t_intakeForm("SaveContinue")
                          : t_intakeForm("Save")}
                    </p>
                  </Button>
                </>
              ) : (
                nextForm && (
                  <Button
                    onClick={() => setSelectedForm(nextForm)}
                    className={cn(
                      "relative overflow-hidden rounded-md px-12 py-3 min-w-[120px]",
                      isIntakeBeingSaved
                        ? "bg-black"
                        : "bg-black hover:bg-black/90",
                    )}
                  >
                    <div
                      className={cn(
                        "absolute inset-0 bg-superscriptBlue transition-transform ease-in-out",
                        isIntakeBeingSaved
                          ? "translate-x-0"
                          : "-translate-x-full",
                      )}
                      style={{ transitionDuration: "5s" }}
                    />

                    <p className="text-white text-sm relative z-10">
                      {isIntakeBeingSaved
                        ? t_intakeForm("Saving")
                        : nextForm
                          ? t_intakeForm("NextForm")
                          : ""}
                    </p>
                  </Button>
                )
              )}
            </div>
          </div>
        )}
      </form>

      <div className="absolute right-8 print:hidden top-[24px] flex gap-2">
        <Button
          aria-disabled={isGeneratingPDF}
          onClick={async () => {
            await handlePrintReceipt();
            trackEvent({
              category: EVENT_CATEGORIES.INTERACTION,
              context: EVENT_CONTEXTS.APPOINTMENT,
              action: "download_individual_intake_form_clicked",
            });
          }}
          className="px-4 rounded-full flex items-center gap-2 border bg-white hover:bg-white border-[#e1e1e1] hover:border-superscriptBlue hover:text-superscriptBlue group transition-colors"
        >
          {instance.loading || isGeneratingPDF ? (
            <p className="text-sm text-black group-hover:text-superscriptBlue transition-colors">
              Generating PDF
            </p>
          ) : (
            <>
              <DownloadIcon
                size={18}
                className="text-[#6d757f] group-hover:text-superscriptBlue transition-colors"
              />
              <p className="text-sm text-black group-hover:text-superscriptBlue transition-colors">
                Download
              </p>
            </>
          )}
        </Button>
        <Button
          onClick={() => setSelectedForm(null)}
          className="px-4 rounded-full flex items-center gap-2 border bg-white hover:bg-white border-[#e1e1e1] hover:border-superscriptBlue hover:text-superscriptBlue group transition-colors"
        >
          <X
            size={18}
            className="text-[#6d757f] group-hover:text-superscriptBlue transition-colors"
          />
        </Button>
      </div>
    </>
  );
};

export const PrintableIntakeForm = ({
  intakeForm,
  consumer,
  visitData,
  isAdmin,
  logo,
  userLocale,
  appointmentId,
  globalIntakeData,
  hiddenQuestions,
}: {
  consumer: Consumer;
  intakeForm: { id: number; name: string };
  visitData?: visitData;
  isAdmin: boolean;
  logo?: { url: string; altText: string };
  userLocale: LanguageCode;
  appointmentId: string;
  globalIntakeData?: IntakeData[];
  hiddenQuestions?: Set<number>;
}) => {
  const intakeData = globalIntakeData
    ? globalIntakeData
    : useIntakeForm([intakeForm.id], appointmentId, userLocale).intakeData;

  const completionDate = intakeData.at(0)?.response?.createdAt;

  const index = intakeData.findIndex((data) => data.form.id === intakeForm.id);

  const {
    intakeFormQuestions,
    defaultValues,
    tempHiddenQuestions,
    sectionedQuestions,
  } = useMemo(
    () => formatIntakeForm(intakeData, {}, index),
    [intakeData, index],
  );

  const filteredSectionedQuestions = useMemo(() => {
    return sectionedQuestions.map((section) => ({
      ...section,
      questions: section.questions.filter(
        (question) => !tempHiddenQuestions.has(question.intakeQuestion.id),
      ),
    }));
  }, [sectionedQuestions, tempHiddenQuestions]);

  const t = useTranslations("IntakeFormStates");

  const dynamicFormSchema = useMemo(
    () => createDynamicFormSchema(intakeFormQuestions),
    [intakeFormQuestions],
  );

  const printDate = completionDate ? parseISO(completionDate) : new Date();

  const formSectionsRef = useRef<RefMap>({});

  // Initialize or update refs for signature section divs
  for (const x of intakeFormQuestions) {
    if (!formSectionsRef.current[x.id]) {
      formSectionsRef.current[x.id] = React.createRef<HTMLDivElement>();
    }
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const form = useForm<Partial<any>>({
    shouldUseNativeValidation: true,
    resolver: zodResolver(dynamicFormSchema),
    defaultValues,
  });

  const {
    register,
    control,
    reset,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = form;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    reset(defaultValues, {
      keepErrors: true,
      keepDirtyValues: true,
    });
  }, [defaultValues]);

  return (
    <>
      <table>
        <thead>
          <tr>
            <td>
              <div className="header-space">&nbsp;</div>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <form>
                <div className="flex flex-col mt-[-60px]">
                  <p className="text-sm print:hidden">
                    Fields marked with * are required.
                  </p>
                  {filteredSectionedQuestions.map((section, i) => (
                    <IntakeFormQuestion
                      clearErrors={() => {}}
                      hiddenQuestions={new Set()}
                      setHiddenQuestions={() => {}}
                      key={section.section}
                      index={i}
                      sectionName={section.section}
                      sectionedFormQuestion={section.questions}
                      questionRef={formSectionsRef}
                      control={control}
                      register={register}
                      errors={errors}
                      getValues={getValues}
                      setValue={setValue}
                      trigger={trigger}
                      isAdmin={isAdmin}
                      isPrint={true}
                      initiateAutosave={() => {}}
                    />
                  ))}
                  {isAdmin && ( // Temporary solution to show the nurse attestation
                    <div className="grid grid-cols-4 gap-8 mt-4 w-[80%] fixed bottom-4">
                      <div className="col-span-3">
                        <p className="text-sm">Nurse Attestation</p>
                        <Separator
                          orientation="horizontal"
                          className="mt-8 bg-black border-black"
                        />
                      </div>
                      <div>
                        <p className="text-sm">Date</p>
                        <Separator
                          orientation="horizontal"
                          className="mt-8 bg-black border-black"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>
              <div className="footer-space">&nbsp;</div>
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="header">
        <div className="flex flex-row justify-between">
          <div className="text-2xl font-medium">{consumerStamp(consumer)}</div>
          {logo && <img className="h-8" src={logo.url} alt={logo.altText} />}
          <div>
            <p className="text-2xl font-medium">{intakeForm.name}</p>
            <p className="text-black font-light text-sm">
              {format(printDate, "PPPp")}
            </p>
          </div>
        </div>
        <Separator orientation="horizontal" className="mt-8" />
      </div>
      <div className="footer">
        {visitData?.location && (
          <div>
            <p>{visitData.location.locationName}</p>
            <p>{visitData.location.address}</p>
          </div>
        )}
      </div>
    </>
  );
};

const consumerStamp = (consumer: Consumer) => {
  return (
    <div>
      <div className="font-bold">{`${consumer.firstname} ${consumer.lastname}`}</div>
      <div className="font-light text-sm">
        DOB: {convertToMMddYYYY(consumer.dob)}
      </div>
      <div className="font-light text-sm">
        Age: {differenceInYears(new Date(), parseISO(consumer.dob))}
      </div>
    </div>
  );
};
